import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import NewRoleSearch from './role-creation-search';
import NewRoleTable from './role-creation-table';
import NewRoleDialog from './role-creation-dialog';
import RoleService from '../../services/user-role/role-service';
import { RolesSummaryDTO } from '../../models/user-role/role-model';
import './role-creation.css';

const NewRoleMain: React.FC = () => {
    const [roles, setRoles] = useState<RolesSummaryDTO[]>([]);

    const [filteredRoles, setFilteredRoles] = useState<RolesSummaryDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [roleDialogVisible, setRoleDialogVisible] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState<{ roleId: number, name: string, description?: string } | undefined>(undefined);
    const [isEditMode, setIsEditMode] = useState(false);

    const [tableKey, setTableKey] = useState(0);

    const [filterRoleId, setFilterRoleId] = useState<number | null>(null);

    const toast = useRef<Toast>(null);

    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        setLoading(true);
        try {
            const data = await RoleService.getRoles();
            setRoles(data || []);
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load roles' });
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = roles;
        if (filterRoleId) {
            filtered = filtered.filter(role => role.roleId === filterRoleId);
        }
        setFilteredRoles(filtered);
    };

    useEffect(() => {
        applyFilters();
    }, [roles, filterRoleId]);

    const handleSearch = (selectedRoleId: number | null) => {
        setFilterRoleId(selectedRoleId);
    };

    const handleReset = () => {
        setFilterRoleId(null);
        setTableKey(prev => prev + 1);
    };

    const openNew = () => {
        setRoleToEdit(undefined);
        setIsEditMode(false);
        setRoleDialogVisible(true);
    };

    const handleEdit = (role: RolesSummaryDTO) => {
        setRoleToEdit({ roleId: role.roleId, name: role.name, description: role.description });
        setIsEditMode(true);
        setRoleDialogVisible(true);
    };

    const handleDelete = async (role: RolesSummaryDTO) => {
        try {
            const response = await RoleService.deleteRole(role.roleId);
            if (response.isSuccess) {
                toast.current?.show({ severity: 'success', summary: 'Successful', detail: 'Role Deleted' });
                loadRoles();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to delete role' });
            }
        } catch (error) {
            console.error(error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'An error occurred' });
        }
    };

    return (
        <div className="flex flex-col min-h-screen font-sans bg-gray-50 pt-2 px-3 sm:px-4 md:px-6 pb-6">
            <Toast ref={toast} />
            <main className="flex-1 max-w-7xl mx-auto w-full animate-fade-in">

                {/* Modern Page Header */}
                <div className="role-creation-header">
                    <div className="role-header-icon-wrapper">
                        <i className="pi pi-users text-4xl"></i>
                    </div>
                    <div>
                        <h1 className="role-header-title">
                            Role Creation
                        </h1>
                        <p className="role-header-subtitle">
                            Create, manage, and configure system roles with ease.
                        </p>
                    </div>
                </div>

                <NewRoleSearch
                    onSearch={handleSearch}
                    onReset={handleReset}
                    onAddClick={openNew}
                />

                <NewRoleTable
                    key={tableKey}
                    data={filteredRoles}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    toast={toast}
                />

                <NewRoleDialog
                    visible={roleDialogVisible}
                    onHide={() => setRoleDialogVisible(false)}
                    onSaveSuccess={() => loadRoles()}
                    editMode={isEditMode}
                    initialData={roleToEdit}
                    toast={toast}
                />
            </main>
        </div>
    );
};

export default NewRoleMain;
